import { useState } from 'react';
import {
  useCreateUpdateGenericShareholderMutation,
  useCreateUpdateGenericShareholdingMutation,
} from '../../../../../gql/generated/graphql';

import { Button, Input, Paper, Typography } from '@mui/material';

const hostUrl = process.env.REACT_APP_FILESERVER_BACKEND;

type Props = {
  companyId: number;
};

const ShareholdingFileUpload = ({ companyId }: Props) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [createShareholder] = useCreateUpdateGenericShareholderMutation();
  const [createShareholding] = useCreateUpdateGenericShareholdingMutation();

  const handleFileChange = event => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage('Please select a file first.');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('uploadFiles', selectedFile);

    try {
      const response = await fetch(`${hostUrl}/${companyId}/upload?fileMeanings=shareholder_list`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      setMessage('File uploaded successfully: ' + data.fileName);

      // Parse PDF client-side (extract text) and call AI proxy for structured shareholders
      const pdfArrayBuffer = await selectedFile.arrayBuffer();
      const textBlob = new Blob([pdfArrayBuffer]);
      // Simple heuristic: send the first N KB as plain text to the LLM; for production, integrate pdfjs-dist
      const reader = new FileReader();
      const textContent: string = await new Promise(resolve => {
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsText(textBlob);
      });

      const backendUrl = process.env.REACT_APP_FILESERVER_BACKEND || 'http://localhost:5001/files';
      const backendBaseUrl = backendUrl.replace('/files', '');
      const aiProxyUrl = `${backendBaseUrl}/ai/proxy/openai`;
      // Auth token is injected into localStorage from REACT_APP_API_AUTH_TOKEN in index.tsx
      const authToken = localStorage.getItem('API_AUTH_TOKEN') || 'dev-local-api-token';
      const systemPrompt =
        'You are an expert at extracting shareholder lists from company shareholder PDFs. Return strict JSON.';
      // Limit to ~100k characters (~25k tokens) to stay within model limits
      // Shareholder lists are typically in the first few pages
      const maxChars = 100000;
      const truncatedText = textContent.slice(0, maxChars);
      const userPrompt = `Extract shareholders from the following text. Return JSON with schema { shareholders: [ { name: string, typeOfInvestor?: string, yearBornOrFounded?: number, holdingPercent?: number } ] }. If you cannot parse, return an empty array. Text:\n\n${truncatedText}`;
      const proxyResp = await fetch(aiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          response_format: { type: 'json_object' },
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.1,
          max_tokens: 4000,
        }),
      });
      if (proxyResp.ok) {
        const proxyJson = await proxyResp.json();
        console.log('AI proxy response:', proxyJson);
        let parsed: any = null;
        try {
          parsed = proxyJson?.choices?.[0]?.message?.content ? JSON.parse(proxyJson.choices[0].message.content) : null;
        } catch (parseError) {
          console.error('Failed to parse AI response:', parseError);
        }
        console.log('Parsed shareholders:', parsed);
        const shareholders: any[] = parsed?.shareholders || [];
        console.log(`Processing ${shareholders.length} shareholders`);

        let createdCount = 0;
        for (const sh of shareholders) {
          const name = sh?.name?.toString()?.trim();
          if (!name) continue;
          const yearBornOrFounded = typeof sh?.yearBornOrFounded === 'number' ? sh.yearBornOrFounded : null;
          const typeOfInvestor = sh?.typeOfInvestor ? String(sh.typeOfInvestor) : null;
          const holdingPercent = typeof sh?.holdingPercent === 'number' ? sh.holdingPercent : null;

          console.log('Creating shareholder:', { name, typeOfInvestor, yearBornOrFounded, holdingPercent });

          // Upsert shareholder
          try {
            const shRes = await createShareholder({
              variables: {
                name,
                typeOfInvestor: typeOfInvestor || undefined,
                yearBornOrFounded: yearBornOrFounded || undefined,
                genericCompanyId: companyId,
              },
            });
            console.log('Shareholder creation response:', shRes);
            const shareholderId = shRes.data?.createUpdateGenericShareholder?.dbId;
            if (shareholderId && holdingPercent !== null) {
              const holdingRes = await createShareholding({
                variables: {
                  genericCompanyId: companyId,
                  genericShareholderId: shareholderId,
                  holding: holdingPercent,
                },
              });
              console.log('Shareholding creation response:', holdingRes);
              createdCount++;
            }
          } catch (mutationError) {
            console.error('Failed to create shareholder/shareholding:', mutationError);
          }
        }
        setMessage(prev => prev + ` | ${createdCount} shareholders saved`);
      } else {
        // Non-fatal: keep upload success
        const errorJson = await proxyResp.json().catch(() => ({}));
        console.error('AI proxy error response:', errorJson);
        setMessage(prev => prev + ' | Parsing failed: ' + (errorJson?.error || 'Unknown error'));
      }
    } catch (error) {
      setMessage('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 400 }}>
      <Typography variant="h6" gutterBottom>
        Upload File
      </Typography>
      <Input type="file" onChange={handleFileChange} fullWidth sx={{ mb: 2 }} />
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleUpload}
        disabled={uploading || !companyId}
        sx={{ mt: 1 }}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </Button>
      {message && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </Paper>
  );
};

export default ShareholdingFileUpload;
